/* Nexa — first-order offer timer, price display, cart bundle toggle, reveal animations.
   The 20-minute window is real: it starts on a visitor's first product view, is stored per device,
   and when it ends the code is removed from the cart and the offer UI switches off. It never restarts. */
(() => {
  if (window.__nexaOffer) return;
  window.__nexaOffer = true;

  const cfg = window.NexaOffer || {};
  const CODE = cfg.code || 'FIRSTRITUAL30';
  const PCT = cfg.percent || 30;
  const DUR = (cfg.minutes || 20) * 60 * 1000;
  const KEY = 'nexa_first_offer_v1';
  const eligible = !cfg.returningCustomer;

  const store = {
    get() { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } },
    set(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {} },
  };

  const fmt = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
  const money = (cents) => {
    const v = (cents / 100).toFixed(2);
    const withSep = (dec, th) => { const [i, f] = v.split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + dec + f; };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => k === 'amount_with_comma_separator' ? withSep(',', '.') : k === 'amount_no_decimals' ? String(Math.round(cents / 100)) : withSep('.', ','));
  };

  let state = store.get();
  const onProduct = !!document.querySelector('[data-nexa-offer]');
  if (eligible && !state && onProduct) { state = { start: Date.now() }; store.set(state); }

  const remaining = () => (state && eligible ? Math.max(0, state.start + DUR - Date.now()) : 0);
  const active = () => remaining() > 0;

  /* ---------- cart helpers ---------- */
  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());
  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const codesOf = (cart) => (cart.discount_codes || []).map((d) => d.code);

  async function syncCode() {
    try {
      const cart = await getCart();
      const codes = codesOf(cart);
      const has = codes.some((c) => c.toUpperCase() === CODE);
      if (active() && !has) await post('/cart/update.js', { discount: [...codes, CODE].join(',') });
      if (!active() && has && state) await post('/cart/update.js', { discount: codes.filter((c) => c.toUpperCase() !== CODE).join(',') });
    } catch (e) {}
  }

  /* ---------- UI ---------- */
  function paint() {
    const ms = remaining();
    const on = ms > 0;
    const m = String(Math.floor(ms / 60000)).padStart(2, '0');
    const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
    document.documentElement.classList.toggle('nexa-offer-on', on);
    document.documentElement.classList.toggle('nexa-offer-off', !on);
    document.querySelectorAll('[data-nexa-timer]').forEach((el) => { el.textContent = `${m}:${s}`; });
    document.querySelectorAll('[data-nexa-offer-price]').forEach((el) => {
      const cents = parseInt(el.dataset.nexaOfferPrice, 10);
      if (!isNaN(cents)) el.textContent = money(Math.round(cents * (100 - PCT) / 100));
    });
    document.querySelectorAll('[data-nexa-offer-save]').forEach((el) => {
      const cents = parseInt(el.dataset.nexaOfferSave, 10);
      if (!isNaN(cents)) el.textContent = money(Math.round(cents * PCT / 100));
    });
    return on;
  }

  let wasOn = paint();
  if (wasOn) syncCode();
  const tick = setInterval(() => {
    const on = paint();
    if (wasOn && !on) { syncCode(); clearInterval(tick); }
    wasOn = on;
  }, 1000);

  // Re-apply the code after items are added (cart may have been created fresh).
  document.addEventListener('submit', (e) => { if (e.target && /\/cart\/add/.test(e.target.action || '')) setTimeout(syncCode, 1200); }, true);
  document.addEventListener('cart:lines-update', () => setTimeout(syncCode, 1200));

  /* ---------- inject cart upgrade cards (rendered server-side via the Section Rendering API) ---------- */
  let injecting = false, injTimer = null;
  async function injectUpgrades() {
    const tables = document.querySelectorAll('cart-items-component .cart-items__table');
    if (!tables.length) return;
    injecting = true;
    try {
      const html = await fetch('/?section_id=nexa-cart-upgrade', { credentials: 'same-origin' }).then((r) => r.text());
      const tmp = document.createElement('div'); tmp.innerHTML = html;
      const node = tmp.querySelector('.nexa-upgrades');
      document.querySelectorAll('cart-items-component .cart-items__table').forEach((t) => {
        const wrap = t.parentElement;
        wrap.querySelectorAll(':scope > .nexa-upgrades').forEach((n) => n.remove());
        if (node) t.after(node.cloneNode(true));
      });
    } catch (e) {}
    setTimeout(() => { injecting = false; }, 50);
  }
  const queueInject = () => { clearTimeout(injTimer); injTimer = setTimeout(injectUpgrades, 250); };
  new MutationObserver((muts) => {
    if (injecting) return;
    if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && !(n.classList && n.classList.contains('nexa-upgrades')) && (n.matches('.cart-items__table, cart-items-component *') || n.querySelector && n.querySelector('.cart-items__table'))))) queueInject();
  }).observe(document.body, { childList: true, subtree: true });
  queueInject();

  /* ---------- cart bundle toggle ---------- */
  async function refreshCart() {
    queueInject();
    try {
      const mod = await import('@theme/section-renderer');
      const comps = document.querySelectorAll('cart-items-component[data-section-id]');
      if (!comps.length) return location.reload();
      comps.forEach((c) => mod.sectionRenderer.renderSection(c.dataset.sectionId, { cache: false, mode: c.hasAttribute('data-drawer') ? 'hydration' : 'full' }));
    } catch (e) { location.reload(); }
  }

  document.addEventListener('change', async (e) => {
    const input = e.target.closest && e.target.closest('[data-nexa-bundle-toggle]');
    if (!input) return;
    const card = input.closest('.nexa-upgrade');
    card && card.classList.add('is-busy');
    const from = input.dataset.from, to = input.dataset.to, qty = parseInt(input.dataset.qty || '1', 10);
    try {
      if (input.checked) {
        await post('/cart/add.js', { items: [{ id: Number(to), quantity: qty, properties: { _nexa_from: from, _nexa_from_title: input.dataset.title || '' } }] });
        await post('/cart/update.js', { updates: { [from]: 0 } });
      } else {
        await post('/cart/change.js', { id: input.dataset.key, quantity: 0 });
        await post('/cart/add.js', { items: [{ id: Number(from), quantity: qty }] });
      }
      await syncCode();
    } catch (err) {}
    refreshCart();
  });

  /* ---------- reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
  }, { threshold: 0.15 }) : null;
  const observe = () => document.querySelectorAll('.nx-reveal:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  observe();

  /* ---------- count-up numbers ---------- */
  document.querySelectorAll('[data-nexa-count]').forEach((el) => {
    const target = parseFloat(el.dataset.nexaCount);
    if (isNaN(target) || !io) return;
    const run = new IntersectionObserver(([en]) => {
      if (!en.isIntersecting) return; run.disconnect();
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / 1200);
        el.textContent = money(Math.round(target * 100 * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.6 });
    run.observe(el);
  });

  /* ---------- sticky bar visibility ---------- */
  const sticky = document.querySelector('.nx-sticky');
  const buy = document.querySelector('[data-nexa-offer]') || document.querySelector('main form[action*="/cart/add"]');
  if (sticky && buy && io) {
    new IntersectionObserver(([en]) => sticky.classList.toggle('is-shown', !en.isIntersecting && en.boundingClientRect.top < 0), { threshold: 0 }).observe(buy);
  }

  /* ---------- before/after slider ---------- */
  document.querySelectorAll('.nx-ba').forEach((ba) => {
    const r = ba.querySelector('input[type=range]');
    r && r.addEventListener('input', () => ba.style.setProperty('--pos', r.value + '%'));
  });
})();
